import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { subscribeNewsletter } from "../lib/api";
import { Reveal } from "./Motion";

const Newsletter = () => {
  const { t, lang } = useLang();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus("loading");
    try {
      const res = await subscribeNewsletter({ email, language: lang });
      setStatus(res.status === "already_subscribed" ? "already" : "success");
      setEmail("");
    } catch {
      setStatus("idle");
    }
  };

  return (
    <section className="relative overflow-hidden bg-obsidian py-28 md:py-40" data-testid="newsletter-section">
      <div className="pointer-events-none absolute left-1/2 top-0 h-[400px] w-[700px] -translate-x-1/2 rounded-full bg-champagne/5 blur-[120px]" />
      <div className="mx-auto max-w-3xl px-6 text-center md:px-12">
        <Reveal>
          <span className="text-xs uppercase tracking-[0.3em] text-champagne">{t.newsletter.overline}</span>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-serif text-4xl font-light leading-tight md:text-6xl">{t.newsletter.title}</h2>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mx-auto mt-6 max-w-xl text-base font-light leading-relaxed text-white/60 md:text-lg">
            {t.newsletter.sub}
          </p>
        </Reveal>

        <Reveal delay={0.3}>
          {status === "success" || status === "already" ? (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-10 flex items-center justify-center gap-3 text-champagne"
              data-testid="newsletter-success"
            >
              <Check size={20} />
              <span className="text-base">{status === "already" ? t.newsletter.already : t.newsletter.success}</span>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="mx-auto mt-10 flex max-w-md flex-col gap-4 sm:flex-row" data-testid="newsletter-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.newsletter.placeholder}
                data-testid="newsletter-email-input"
                className="flex-1 border-b border-white/20 bg-transparent px-2 py-3 text-base text-white placeholder:text-white/40 focus:border-champagne focus:outline-none transition-colors"
              />
              <button
                type="submit"
                disabled={status === "loading"}
                data-testid="newsletter-submit"
                className="group flex items-center justify-center gap-2 border border-champagne bg-champagne px-7 py-3 text-xs uppercase tracking-[0.2em] text-obsidian transition-colors duration-300 hover:bg-transparent hover:text-champagne disabled:opacity-60"
              >
                {t.newsletter.subscribe}
                <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </form>
          )}
        </Reveal>
        <Reveal delay={0.4}>
          <p className="mx-auto mt-6 max-w-md text-xs leading-relaxed text-white/35">{t.newsletter.consent}</p>
        </Reveal>
      </div>
    </section>
  );
};

export default Newsletter;
